import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { ValueDisplayComponent } from './value-display.component';
import { VerifiedValueComponent } from './verified-value.component';
import { bagCheckDateControl, isBagCheckVerified, isBagVerifiable } from '../verification.util';

/**
 * Read display for an `address` answer: the same text as before, plus the BAG check the edit
 * component shows once the address was verified (verification.util.ts). No check, no icon.
 *
 * Presentational: both inputs come from the row. The verified state also depends on the field's
 * own form tree (the check date lives on the ROOT form, not on `value()`), so it is tracked
 * through the root control's `valueChanges` rather than through `value()` alone — a check date
 * written after first render (the user runs BAG verification while the read view is already
 * open) would otherwise never surface until some unrelated change forced the computed to re-run.
 *
 * The icon and its layout live in `VerifiedValueComponent`; this component only decides `verified`.
 */
@Component({
  selector: 'dd-address-display',
  imports: [ValueDisplayComponent, VerifiedValueComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — same reason as ValueDisplayComponent: no box inside the value span.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    <dd-verified-value
      [verified]="verified()"
      icon="verified"
      label="Geverifieerd via BAG"
      iconDataCy="readonly-bag-verified"
    >
      <dd-value-display [field]="field()" [value]="value()" />
    </dd-verified-value>
  `,
})
export class AddressDisplayComponent extends ReadonlyDisplayBase {
  /**
   * The root form's `meta-data.bag-last-check-date`, re-read on every `valueChanges` emission
   * (seeded with the control's current value via `startWith`) instead of once at construction —
   * see the class docblock for why a plain field/value snapshot is not enough here.
   */
  readonly #bagCheckDate = toSignal(
    toObservable(this.field).pipe(
      switchMap(field => {
        const control = bagCheckDateControl(field);
        return control ? control.valueChanges.pipe(startWith(control.value)) : of(undefined);
      })
    ),
    { initialValue: undefined }
  );

  readonly verified = computed(
    () => isBagVerifiable(this.field()) && isBagCheckVerified(this.#bagCheckDate(), this.value())
  );
}
